/**
 * Funzioni API per i Report SRP e i timer SLA (E10.6/E10.7 — srp-service).
 * Usano createServiceClient('srp') come da pattern slice 1/2/3.
 */

import { createServiceClient } from '../../api/client.js';
import type {
  SrpReport,
  CreateSrpReportInput,
  SaveThreadInput,
  NotificationStep,
  ThreadStatus,
  SlaTimer,
  CorrectSlaDetectionInput,
  AttachCveInput,
} from './types.js';
import type { ReportCategory } from './category.js';

/** Client parzialmente applicato per l'srp-service. */
const srpApi = createServiceClient('srp');

// ---------------------------------------------------------------------------
// Report SRP
// ---------------------------------------------------------------------------

/**
 * Recupera la lista di tutti i report SRP.
 * GET /srp/reports
 */
export function fetchSrpReports(): Promise<SrpReport[]> {
  return srpApi<SrpReport[]>('/srp/reports');
}

/**
 * Recupera il dettaglio di un report SRP per ID.
 * GET /srp/reports/:id
 */
export function fetchSrpReport(id: number): Promise<SrpReport> {
  return srpApi<SrpReport>(`/srp/reports/${id}`);
}

/**
 * Crea un nuovo report SRP in stato draft.
 * POST /srp/reports
 */
export function createSrpReport(input: CreateSrpReportInput): Promise<SrpReport> {
  return srpApi<SrpReport>('/srp/reports', { method: 'POST', body: input });
}

/**
 * Aggiorna le note libere e/o la checklist del thread di notifica CRA Art.14.
 * PATCH /srp/reports/:id/thread
 */
export function saveThread(id: number, body: SaveThreadInput): Promise<SrpReport> {
  return srpApi<SrpReport>(`/srp/reports/${id}/thread`, { method: 'PATCH', body });
}

/**
 * Sposta il report tra le categorie ENISA (Actively Exploited Vulnerability / Severe Incident).
 * PATCH /srp/reports/:id/category
 */
export function setReportCategory(id: number, category: ReportCategory): Promise<SrpReport> {
  return srpApi<SrpReport>(`/srp/reports/${id}/category`, { method: 'PATCH', body: { category } });
}

/**
 * Registra uno step CRA Art.14(2) come fatto, con una nota facoltativa. L'analista inoltra a ENISA
 * per conto proprio; qui lo step viene tracciato (audit 'modify').
 * POST /srp/reports/:id/record-step
 */
export function recordReportStep(
  id: number,
  step: NotificationStep,
  note?: string,
): Promise<SrpReport> {
  return srpApi<SrpReport>(`/srp/reports/${id}/record-step`, {
    method: 'POST',
    body: { step, note },
  });
}

/**
 * Annulla la registrazione di uno step non davvero inviato a ENISA: lo step torna da fare e il
 * suo timer SLA riparte. Motivazione obbligatoria (audit 'modify').
 * POST /srp/reports/:id/undo-step
 */
export function undoReportStep(
  id: number,
  step: NotificationStep,
  reason: string,
): Promise<SrpReport> {
  return srpApi<SrpReport>(`/srp/reports/${id}/undo-step`, {
    method: 'POST',
    body: { step, reason },
  });
}

/**
 * Imposta lo stato di risoluzione del thread di notifica CRA Art.14.
 * POST /srp/reports/:id/resolve
 */
export function resolveThread(id: number, resolution: ThreadStatus): Promise<SrpReport> {
  return srpApi<SrpReport>(`/srp/reports/${id}/resolve`, {
    method: 'POST',
    body: { resolution },
  });
}

/**
 * Allega una CVE al report (anche chiuso); 409 se già presente. Tracciato in audit ('modify').
 * POST /srp/reports/:id/cves
 */
export function attachReportCve(id: number, input: AttachCveInput): Promise<SrpReport> {
  return srpApi<SrpReport>(`/srp/reports/${id}/cves`, { method: 'POST', body: input });
}

/**
 * Rimuove una CVE dal report; 404 se non presente. Tracciato in audit ('modify').
 * DELETE /srp/reports/:id/cves/:cveId
 */
export function detachReportCve(id: number, cveId: string): Promise<SrpReport> {
  return srpApi<SrpReport>(`/srp/reports/${id}/cves/${encodeURIComponent(cveId)}`, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// Timer SLA
// ---------------------------------------------------------------------------

/**
 * Recupera la lista dei timer SLA con countdown calcolato.
 * GET /sla/timers
 */
export function fetchSlaTimers(): Promise<SlaTimer[]> {
  return srpApi<SlaTimer[]>('/sla/timers');
}

/**
 * Corregge manualmente l'istante di rilevazione (ancora del clock CRA Art.14) di un timer SLA.
 * PATCH /sla/timers/:id/detected-at — 400 se data futura/non valida o motivazione vuota.
 */
export function correctSlaDetection(
  timerId: number,
  input: CorrectSlaDetectionInput,
): Promise<SlaTimer> {
  return srpApi<SlaTimer>(`/sla/timers/${timerId}/detected-at`, { method: 'PATCH', body: input });
}
